/**
 * Adapter functions to work with existing Firebase rules without modifying them.
 * These helpers adapt our code to match the expected structure in the rules.
 */

import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from './config';

/**
 * Saves user experience data in a format compatible with existing Firebase rules.
 * Tries multiple approaches to find one that works with the permissions.
 * 
 * @param {string} userId - The user's ID
 * @param {object} experienceData - Experience data to save
 * @returns {Promise<boolean>} - Success status
 */
export const saveUserExperience = async (userId, experienceData) => {
  console.log(`⏳ Attempting to save experience for user: ${userId}`, experienceData);
  
  // First try: Use the experiences collection with userId in document ID
  try {
    const docId = `${userId}_experience`;
    const experienceRef = doc(firestore, 'experiences', docId);
    await setDoc(experienceRef, {
      ...experienceData,
      userId, // Explicitly include userId for permission checks
      updatedAt: new Date()
    }, { merge: true });
    console.log('✅ Saved to experiences collection successfully');
    return true;
  } catch (error) {
    console.error('❌ Failed to save to experiences collection:', error);
    console.log('Error code:', error.code, 'Error message:', error.message);
    
    // Second try: Use the userExperience collection
    try {
      const experienceRef = doc(firestore, 'userExperience', userId);
      await setDoc(experienceRef, {
        ...experienceData,
        updatedAt: new Date()
      }, { merge: true });
      console.log('✅ Saved to userExperience collection successfully');
      return true;
    } catch (secondError) {
      console.error('❌ Failed to save to userExperience collection:', secondError);
      console.log('Error code:', secondError.code, 'Error message:', secondError.message);
      
      // Last resort: Store in userProfiles which definitely has proper permissions
      try {
        const profileRef = doc(firestore, 'userProfiles', userId);
        
        // Create experience update data
        const updateData = {
          experience: experienceData,
          updatedAt: new Date()
        };
        
        console.log('🔍 Attempting to save to userProfiles as fallback with data:', updateData);
        
        await setDoc(profileRef, updateData, { merge: true });
        
        // Verify the data was actually written
        const docSnapshot = await getDoc(profileRef);
        if (docSnapshot.exists()) {
          const savedData = docSnapshot.data();
          console.log('📋 Current userProfiles document data after save:', savedData);
          if (savedData.experience) {
            console.log('✅ Confirmed experience data is in userProfiles');
            return true;
          } else {
            console.error('⚠️ No experience data found in userProfiles after save');
            return false;
          }
        } else {
          console.error('⚠️ userProfiles document does not exist after save attempt');
          return false;
        }
      } catch (finalError) {
        console.error('❌ ALL ATTEMPTS FAILED! Final error:', finalError);
        console.log('Error code:', finalError.code, 'Error message:', finalError.message);
        return false;
      }
    }
  }
};

/**
 * Saves user job preferences in a format compatible with existing Firebase rules.
 * Tries multiple approaches to find one that works with the permissions.
 * Enhanced with detailed logging and checks.
 * 
 * @param {string} userId - The user's ID
 * @param {object} preferencesData - Job preferences data to save
 * @returns {Promise<boolean>} - Success status
 */
export const saveUserJobPreferences = async (userId, preferencesData) => {
  console.log(`⏳ Attempting to save job preferences for user: ${userId}`, preferencesData);
  let savedSuccessfully = false;
  
  // Add an identifier field to easily find data by type
  const dataToSave = {
    ...preferencesData,
    _data_type: 'job_preferences',
    _version: '1.0',
    updatedAt: new Date()
  };
  
  // Try the primary approach: jobPreferences collection with userId in document ID
  try {
    const docId = `${userId}_preferences`;
    const prefsRef = doc(firestore, 'jobPreferences', docId);
    console.log(`🔍 Trying to save to jobPreferences/${docId}`);
    
    await setDoc(prefsRef, {
      ...dataToSave,
      userId, // Explicitly include userId for permission checks
    }, { merge: true });
    
    console.log('✅ Saved to jobPreferences collection successfully');
    savedSuccessfully = true;
  } catch (error) {
    console.error('❌ Failed to save to jobPreferences collection:', error);
    console.log('Error code:', error.code, 'Error message:', error.message);
    
    // Try alternate collection name
    try {
      const prefsRef = doc(firestore, 'userJobPreferences', userId);
      console.log(`🔍 Trying to save to userJobPreferences/${userId}`);
      
      await setDoc(prefsRef, {
        ...dataToSave,
      }, { merge: true });
      
      console.log('✅ Saved to userJobPreferences collection successfully');
      savedSuccessfully = true;
    } catch (secondError) {
      console.error('❌ Failed to save to userJobPreferences collection:', secondError);
      console.log('Error code:', secondError.code, 'Error message:', secondError.message);
    }
  }
  
  // Always update the user profile as a fallback
  try {
    const profileRef = doc(firestore, 'userProfiles', userId);
    console.log(`🔍 Trying to save to userProfiles/${userId} as final fallback`);
    
    // Create preferences update data with multiple alternative property names
    // This increases the chance that at least one format will be acceptable
    const updateData = {
      // Try multiple field names to increase chances of success
      jobPreferences: dataToSave,
      preferences: dataToSave,
      prefData: dataToSave,
      userPreferences: dataToSave,
      isProfileComplete: true,
      lastUpdated: new Date(),
      _debug_save_attempt: new Date().toString(),
      _data_complete: true
    };
    
    console.log('🔍 Attempting to save to userProfiles with data:', updateData);
    
    await setDoc(profileRef, updateData, { merge: true });
    
    // Verify the data was actually written
    const docSnapshot = await getDoc(profileRef);
    if (docSnapshot.exists()) {
      const savedData = docSnapshot.data();
      console.log('📋 Current userProfiles document data after save:', savedData);
      
      // Check for any of our alternative field names
      const preferencesSaved = 
        savedData.jobPreferences || 
        savedData.preferences || 
        savedData.prefData || 
        savedData.userPreferences;
        
      if (preferencesSaved) {
        console.log('✅ Confirmed job preferences data is in userProfiles under one of the field names');
        return true;
      } else {
        console.error('⚠️ No job preferences data found in userProfiles under any field name');
        return savedSuccessfully;
      }
    } else {
      console.error('⚠️ userProfiles document does not exist after save attempt');
      return savedSuccessfully;
    }
  } catch (profileError) {
    console.error('❌ ALL ATTEMPTS FAILED! Final error:', profileError);
    console.log('Error code:', profileError.code, 'Error message:', profileError.message);
    return savedSuccessfully;
  }
};

/**
 * Creates a properly formatted document path compatible with existing Firebase storage rules.
 * The storage rules expect userId_filename format.
 * 
 * @param {string} collection - Storage collection name
 * @param {string} userId - User ID
 * @param {string} filename - Optional filename
 * @returns {string} Formatted path
 */
export const createStoragePath = (collection, userId, filename = 'profile') => {
  return `${collection}/${userId}_${filename}`;
};

/**
 * Utility function to check if a user's profile exists and read its current data
 * Use this for debugging or verification purposes
 * 
 * @param {string} userId - User ID to check
 * @returns {Promise<object|null>} User profile data or null if not found
 */
export const checkUserProfile = async (userId) => {
  try {
    const profileRef = doc(firestore, 'userProfiles', userId);
    const docSnapshot = await getDoc(profileRef);
    
    if (docSnapshot.exists()) {
      console.log(`📋 User profile for ${userId} exists with data:`, docSnapshot.data());
      return docSnapshot.data();
    } else {
      console.log(`⚠️ No profile found for user ${userId}`);
      return null;
    }
  } catch (error) {
    console.error('❌ Error checking user profile:', error);
    console.log('Error code:', error.code, 'Error message:', error.message);
    return null;
  }
};

/**
 * Test write access to various collections
 * This helps diagnose Firebase security rule issues
 * 
 * @param {string} userId - The user ID to test with
 * @returns {Promise<Object>} Results of testing each collection
 */
export const testCollectionAccess = async (userId) => {
  const results = {};
  const testData = { test: true, timestamp: new Date(), userId };
  
  // Test collections
  const collectionsToTest = [
    'users', 
    'userProfiles',
    'experiences',
    'userExperience',
    'jobPreferences',
    'userJobPreferences'
  ];
  
  console.log(`🔍 Testing write access to ${collectionsToTest.length} collections for user ${userId}...`);
  
  for (const collectionName of collectionsToTest) {
    try {
      // For collections that might need special document ID format
      let docId = userId;
      if (collectionName === 'experiences') {
        docId = `${userId}_experience`;
      } else if (collectionName === 'jobPreferences') {
        docId = `${userId}_preferences`;
      }
      
      console.log(`🔍 Testing write to ${collectionName}/${docId}...`);
      
      // Try to write test data
      const testRef = doc(firestore, collectionName, docId);
      await setDoc(testRef, {
        ...testData,
        _collection: collectionName,
        _testTimestamp: new Date().toString()
      }, { merge: true });
      
      // Verify the write worked
      const docSnapshot = await getDoc(testRef);
      if (docSnapshot.exists() && docSnapshot.data()._testTimestamp) {
        console.log(`✅ Write access CONFIRMED to ${collectionName}`);
        results[collectionName] = { success: true };
      } else {
        console.log(`⚠️ Write appeared to succeed but data not found in ${collectionName}`);
        results[collectionName] = { success: false, error: 'Data not found after write' };
      }
    } catch (error) {
      console.error(`❌ NO write access to ${collectionName}:`, error);
      console.log('Error code:', error.code, 'Error message:', error.message);
      results[collectionName] = { success: false, error: error.message };
    }
  }
  
  console.log('📋 Collection access test results:', results);
  return results;
} 